import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"

export default class extends Controller {
  static targets = ["calendar"]
  static values = {
    eventsUrl: String,
    newLessonUrl: String,
    locale: { type: String, default: "en" },
    membersSearchUrl: String,
    translations: Object,
    canManage: { type: Boolean, default: false }
  }

  connect() {
    this.t = this.translationsValue || {}

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: "timeGridWeek",
      locale: this.localeValue,
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay"
      },
      slotMinTime: "06:00:00",
      slotMaxTime: "22:00:00",
      allDaySlot: false,
      weekends: true,
      nowIndicator: true,
      height: "auto",
      events: this.fetchEvents.bind(this),
      dateClick: this.handleDateClick.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventDidMount: this.styleEvent.bind(this)
    })
    this.calendar.render()

    this._onDocumentClick = (e) => {
      if (this._popover && !this._popover.contains(e.target)) {
        this.closePopover()
      }
    }
    document.addEventListener("click", this._onDocumentClick, true)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick, true)
    this.closePopover()
    if (this.calendar) this.calendar.destroy()
  }

  async fetchEvents(info, successCallback, failureCallback) {
    try {
      const url = new URL(this.eventsUrlValue, window.location.origin)
      url.searchParams.set("start", info.startStr)
      url.searchParams.set("end", info.endStr)

      const response = await fetch(url, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) throw new Error("Failed to fetch events")

      const events = await response.json()
      successCallback(events)
    } catch (error) {
      failureCallback(error)
    }
  }

  handleDateClick(info) {
    if (!this.hasNewLessonUrlValue || !this.newLessonUrlValue) return
    const url = new URL(this.newLessonUrlValue, window.location.origin)
    url.searchParams.set("date", info.dateStr)
    url.searchParams.set("time", info.date.toTimeString().slice(0, 5))
    window.location.href = url.toString()
  }

  handleEventClick(info) {
    info.jsEvent.preventDefault()
    info.jsEvent.stopPropagation()

    const props = info.event.extendedProps
    if (props.blocked) return

    this.closePopover()
    this.showPopover(info.event, info.el)
  }

  styleEvent(info) {
    const props = info.event.extendedProps

    if (props.cancelled) {
      info.el.style.opacity = "0.45"
      info.el.style.textDecoration = "line-through"
    }

    if (props.substitute) {
      info.el.style.borderLeft = "4px solid #f0506e"
    }

    if (props.blocked) {
      info.el.style.opacity = "0.3"
      info.el.style.cursor = "not-allowed"
      info.el.style.pointerEvents = "none"
    }
  }

  showPopover(event, anchorEl) {
    const props = event.extendedProps
    const popover = document.createElement("div")
    popover.className = "calendar-popover uk-card uk-card-default uk-card-small uk-card-body uk-box-shadow-large"
    popover.style.cssText = "position:absolute;z-index:1100;min-width:320px;max-width:400px;border-radius:8px;"

    let html = `<div class="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
      <h5 class="uk-card-title uk-margin-remove">${this.escapeHtml(event.title)}</h5>
      <button class="uk-close" type="button" data-action="close">&times;</button>
    </div>`

    html += `<dl class="uk-description-list uk-description-list-divider uk-margin-small-bottom" style="font-size:0.9rem">`

    if (props.coaches) {
      html += `<dt>${this.t.coaches || "Coaches"}</dt><dd>${this.escapeHtml(props.coaches)}</dd>`
    }
    if (props.location) {
      html += `<dt>${this.t.location || "Location"}</dt><dd>${this.escapeHtml(props.location)}</dd>`
    }
    if (props.group) {
      html += `<dt>${this.t.group || "Group"}</dt><dd>${this.escapeHtml(props.group)}</dd>`
    }
    if (props.dancers) {
      html += `<dt>${this.t.dancers || "Dancers"}</dt><dd>${this.escapeHtml(props.dancers)}</dd>`
    }
    if (props.coach) {
      html += `<dt>${this.t.coaches || "Coach"}</dt><dd>${this.escapeHtml(props.coach)}</dd>`
    }
    if (props.cancelled) {
      html += `<dt>${this.t.cancelled || "Cancelled"}</dt><dd class="uk-text-danger">${this.escapeHtml(props.cancelReason || "—")}</dd>`
    }
    if (props.substitute) {
      html += `<dt>${this.t.substitute_coach || "Substitute"}</dt><dd class="uk-text-warning">${this.escapeHtml(props.substitute)}</dd>`
    }
    html += `</dl>`

    if (this.canManageValue) {
      html += `<div class="popover-actions uk-margin-small-top">`

      if (props.cancelled || props.substitute) {
        html += `<button class="sc-button sc-button-small sc-button-primary uk-width-1-1 uk-margin-small-bottom" data-action="restore">
          <i class="mdi mdi-restore"></i> ${this.t.restore_event || "Restore"}
        </button>`
      }

      if (!props.cancelled && !props.substitute) {
        html += `<button class="sc-button sc-button-small sc-button-danger uk-width-1-1 uk-margin-small-bottom" data-action="show-cancel">
          <i class="mdi mdi-cancel"></i> ${this.t.cancel_event || "Cancel event"}
        </button>`
        html += `<button class="sc-button sc-button-small sc-button-warning uk-width-1-1 uk-margin-small-bottom" data-action="show-substitute">
          <i class="mdi mdi-account-switch"></i> ${this.t.assign_substitute || "Assign substitute"}
        </button>`
      }

      // Cancel form (hidden)
      html += `<div class="cancel-form" style="display:none">
        <hr class="uk-divider-small">
        <div class="uk-margin-small">
          <textarea class="uk-textarea uk-form-small" rows="2" placeholder="${this.t.reason_placeholder || "Reason..."}" data-field="reason"></textarea>
        </div>
        <div class="uk-flex uk-flex-right uk-grid-small" uk-grid>
          <div><button class="sc-button sc-button-small sc-button-default" data-action="hide-forms">${this.t.close || "Close"}</button></div>
          <div><button class="sc-button sc-button-small sc-button-danger" data-action="cancel-event">${this.t.cancel_event || "Cancel"}</button></div>
        </div>
      </div>`

      // Substitute form (hidden)
      html += `<div class="substitute-form" style="display:none">
        <hr class="uk-divider-small">
        <div class="uk-margin-small">
          <label class="uk-form-label"><input type="radio" class="uk-radio" name="sub_type" value="club" checked> ${this.t.substitute_from_club || "Club member"}</label>
        </div>
        <div class="uk-margin-small sub-club-field">
          <input type="text" class="uk-input uk-form-small" data-field="sub-search" placeholder="${this.t.coaches || "Search coach..."}" autocomplete="off">
          <input type="hidden" data-field="substitute_coach_id">
          <div class="sub-results uk-panel" style="max-height:150px;overflow-y:auto;display:none"></div>
        </div>
        <div class="uk-margin-small">
          <label class="uk-form-label"><input type="radio" class="uk-radio" name="sub_type" value="guest"> ${this.t.guest_substitute || "Guest"}</label>
        </div>
        <div class="uk-margin-small sub-guest-field" style="display:none">
          <input type="text" class="uk-input uk-form-small" data-field="substitute_name" placeholder="${this.t.guest_name_placeholder || "Guest name..."}">
        </div>
        <div class="uk-margin-small">
          <textarea class="uk-textarea uk-form-small" rows="2" placeholder="${this.t.reason_placeholder || "Reason..."}" data-field="sub-reason"></textarea>
        </div>
        <div class="uk-flex uk-flex-right uk-grid-small" uk-grid>
          <div><button class="sc-button sc-button-small sc-button-default" data-action="hide-forms">${this.t.close || "Close"}</button></div>
          <div><button class="sc-button sc-button-small sc-button-primary" data-action="submit-substitute">${this.t.save || "Save"}</button></div>
        </div>
      </div>`

      html += `</div>`
    }

    popover.innerHTML = html
    document.body.appendChild(popover)
    this._popover = popover
    this._popoverEvent = event

    this.positionPopover(popover, anchorEl)
    this.bindPopoverActions(popover, event)
  }

  positionPopover(popover, anchor) {
    const rect = anchor.getBoundingClientRect()
    const scrollTop = window.scrollY
    const scrollLeft = window.scrollX

    let top = rect.bottom + scrollTop + 4
    let left = rect.left + scrollLeft

    requestAnimationFrame(() => {
      const pw = popover.offsetWidth
      const ph = popover.offsetHeight
      if (left + pw > window.innerWidth + scrollLeft - 16) {
        left = window.innerWidth + scrollLeft - pw - 16
      }
      if (top + ph > window.innerHeight + scrollTop - 16) {
        top = rect.top + scrollTop - ph - 4
      }
      popover.style.top = `${top}px`
      popover.style.left = `${left}px`
    })
  }

  bindPopoverActions(popover, event) {
    const props = event.extendedProps

    popover.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-action]")
      if (!btn) return

      const action = btn.dataset.action
      switch (action) {
        case "close":
          this.closePopover()
          break
        case "show-cancel":
          popover.querySelector(".cancel-form").style.display = ""
          popover.querySelector(".substitute-form").style.display = "none"
          break
        case "show-substitute":
          popover.querySelector(".substitute-form").style.display = ""
          popover.querySelector(".cancel-form").style.display = "none"
          break
        case "hide-forms":
          popover.querySelector(".cancel-form").style.display = "none"
          popover.querySelector(".substitute-form").style.display = "none"
          break
        case "cancel-event":
          this.submitCancel(props)
          break
        case "restore":
          this.submitRestore(props)
          break
        case "submit-substitute":
          this.submitSubstitute(popover, props)
          break
      }
    })

    popover.querySelectorAll("input[name=sub_type]").forEach((radio) => {
      radio.addEventListener("change", () => {
        const isGuest = radio.value === "guest"
        popover.querySelector(".sub-club-field").style.display = isGuest ? "none" : ""
        popover.querySelector(".sub-guest-field").style.display = isGuest ? "" : "none"
      })
    })

    const searchInput = popover.querySelector("[data-field=sub-search]")
    if (searchInput && this.hasMembersSearchUrlValue) {
      let debounce
      searchInput.addEventListener("input", () => {
        clearTimeout(debounce)
        debounce = setTimeout(() => this.searchMembers(searchInput, popover), 300)
      })
    }
  }

  async searchMembers(input, popover) {
    const query = input.value.trim()
    const results = popover.querySelector(".sub-results")
    if (query.length < 2) {
      results.style.display = "none"
      return
    }

    const url = new URL(this.membersSearchUrlValue, window.location.origin)
    url.searchParams.set("q", query)

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    const data = await response.json()

    if (!data.length) {
      results.style.display = "none"
      return
    }

    results.innerHTML = data
      .map((u) => `<div class="sub-result-item uk-padding-small" style="cursor:pointer;border-bottom:1px solid #e5e5e5" data-id="${u.id}" data-name="${this.escapeHtml(u.full_name || u.name)}">${this.escapeHtml(u.full_name || u.name)}</div>`)
      .join("")
    results.style.display = ""

    results.querySelectorAll(".sub-result-item").forEach((item) => {
      item.addEventListener("click", () => {
        input.value = item.dataset.name
        popover.querySelector("[data-field=substitute_coach_id]").value = item.dataset.id
        results.style.display = "none"
      })
    })
  }

  async submitCancel(props) {
    const reason = this._popover?.querySelector('.cancel-form [data-field="reason"]')?.value || ""
    const url = props.cancelUrl
    if (!url) return

    const body = new FormData()
    body.append("date", props.date)
    if (reason) body.append("reason", reason)

    await this.postAction(url, body)
  }

  async submitRestore(props) {
    let url = props.restoreUrl
    if (!url) return

    const restoreUrl = new URL(url, window.location.origin)
    restoreUrl.searchParams.set("date", props.date)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(restoreUrl, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "application/json"
      }
    })

    if (response.ok) {
      this.closePopover()
      this.calendar.refetchEvents()
    }
  }

  async submitSubstitute(popover, props) {
    const url = props.substituteUrl
    if (!url) return

    const subType = popover.querySelector("input[name=sub_type]:checked")?.value
    const body = new FormData()
    body.append("date", props.date)

    const reason = popover.querySelector('[data-field="sub-reason"]')?.value
    if (reason) body.append("reason", reason)

    if (subType === "guest") {
      const name = popover.querySelector('[data-field="substitute_name"]')?.value
      if (name) body.append("substitute_name", name)
    } else {
      const coachId = popover.querySelector('[data-field="substitute_coach_id"]')?.value
      if (coachId) body.append("substitute_coach_id", coachId)
    }

    await this.postAction(url, body)
  }

  async postAction(url, body, method = "POST") {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method,
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "application/json"
      },
      body
    })

    if (response.ok) {
      this.closePopover()
      this.calendar.refetchEvents()
    }
  }

  closePopover() {
    if (this._popover) {
      this._popover.remove()
      this._popover = null
      this._popoverEvent = null
    }
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
